import { ApiService } from "../api/ApiService.interface.ts";
import { PollingService } from "../api/PollingService/PollingService.ts";
import { AuthService } from "../../auth/AuthService.ts";
import { HttpClient } from "../../http/HttpClient.ts";
import { Subscribable } from "../../Subscribable.ts";
import { AppState, FilterState, Message, ResearchId } from "./AppState.ts";
import {
  ProductEvent,
  ProductEventTarget,
} from "../../product-events/ProductEventTarget.ts";
function getAppContext(
  enableClaMode: boolean,
  enableProductTestingBusinessDomain: boolean,
): "insights_agent" | "cla" | "pla" {
  if (enableClaMode && enableProductTestingBusinessDomain) {
    return "pla";
  }
  if (enableClaMode) {
    return "cla";
  }
  return "insights_agent";
}

export class InsightsAgentService extends Subscribable<AppState> {
  private readonly enableClaMode: boolean;
  private readonly enableProductTestingBusinessDomain: boolean;

  constructor(
    private readonly httpClient: HttpClient,
    readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly pollingService: PollingService,
    private readonly productEvents: ProductEventTarget,
    config: {
      enableClaMode: boolean;
      enableProductTestingBusinessDomain: boolean;
    },
  ) {
    super({
      authState: "loading",
      researchMap: {
        items: {},
        loading: true,
      },
      messages: {
        items: {},
        loading: true,
      },
      fileReferences: {
        items: {},
        loading: true,
      },
      filters: {
        items: {},
        loading: true,
      },
      filterOptions: {
        loading: true,
      },
      knowledgeSources: {
        items: [],
        loading: true,
      },
      researchTypes: {
        items: [],
        loading: true,
      },
      termsAndConditions: {
        status: "loading",
        content: null,
        loading: false,
        accepting: false,
        error: null,
      },
    });
    this.enableClaMode = config.enableClaMode;
    this.enableProductTestingBusinessDomain =
      config.enableProductTestingBusinessDomain;
    this.authService.subscribe(() => {
      this.updateState((draft) => {
        draft.authState = this.authService.getState().authState;
      });
      this.httpClient.setToken(this.authService.getState().token || null);
    });
    this.pollingService
      .startPolling(
        () => this.refreshResearches(),
        10000,
        async () =>
          Object.values(this.getState().researchMap.items).some(
            (research) => research.status === "processing_meta_analysis",
          ),
      )
      .then();
  }

  /**
   * Starts a new meta-analysis by calling the appropriate endpoint.
   *
   * Documentation:
   * https://backstage.dev.ipsosfacto.com/catalog/default/api/vertex-knowledge-source-answers-api-api/definition#/default/start_meta_analysis_knowledge_source_search_and_answers_research__post
   */
  async startMetaAnalysis(payload: {
    knowledgeSourceId: string;
    researchGoal: string;
    additionalQuestions: string;
    researchType?: string;
  }) {
    const result = await this.apiService.createResearch({
      knowledgeSourceId: payload.knowledgeSourceId,
      researchGoal: payload.researchGoal,
      additionalQuestions: payload.additionalQuestions,
      researchType: payload.researchType,
    });

    this.productEvents.dispatchEvent(new ProductEvent("chat_start"));

    // Wait for the research to be properly loaded before returning
    await this.refreshResearches();

    return result;
  }

  async refreshResearchTypes() {
    this.updateState((draft) => {
      draft.researchTypes.loading = true;
    });
    const { data } = await this.apiService.getResearchTypes();
    if (!data) {
      console.log("Error fetching research types");
      this.updateState((draft) => {
        draft.researchTypes.loading = false;
      });
      return;
    }
    this.updateState((draft) => {
      draft.researchTypes.items = data;
      draft.researchTypes.loading = false;
    });
  }

  async refreshResearches() {
    const { data } = await this.apiService.getResearchList();
    if (!data) throw new Error("No data");
    this.updateState((draft) => {
      data.researches.forEach((research) => {
        draft.researchMap.items[research.id] = research;
      });
      draft.researchMap.loading = false;
    });
  }

  async refreshResearchItem(payload: { id: ResearchId }) {
    const { data, success } = await this.apiService.getResearch({
      id: payload.id,
    });
    if (!success) {
      console.log(`Error fetching research ${payload.id}`);
      window.location.href = "/";
      return;
    }
    if (!data) return;
    this.updateState((draft) => {
      draft.researchMap.items[data.id] = data;
      draft.researchMap.loading = false;
    });
  }

  async sendMessage(payload: { researchId: ResearchId; message: string }) {
    this.productEvents.dispatchEvent(new ProductEvent("followup_message_send"));
    this.updateState((draft) => {
      const messages = this.pickResearchMessages(draft, payload);
      messages.push({
        id: "optimistic_message",
        status: "optimistic",
        value: payload.message,
        sender: "user",
        create_time: new Date().toISOString(),
      });
      messages.push({
        id: "pending_message",
        status: "optimistic",
        sender: "agent",
        create_time: new Date().toISOString(),
      });
    });
    await this.apiService.submitMessage(payload);
    await this.refreshMessages({
      researchId: payload.researchId,
    });
  }

  async refreshMessages(payload: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.messages.loading = true;
    });
    const { data, httpStatus } =
      await this.apiService.getResearchMessages(payload);
    if (!data) {
      console.log(
        `Error fetching messages for research ${payload.researchId}`,
        httpStatus,
      );
      return;
    }
    this.updateState((draft) => {
      if (!draft.messages.items[payload.researchId]) {
        draft.messages.items[payload.researchId] = [];
      }
      const existingMessages = draft.messages.items[payload.researchId];
      const newMessages = data.messages;

      newMessages.forEach((newMessage) => {
        const existingMessage = existingMessages.find(
          (m) => m.id === newMessage.id,
        );
        if (!existingMessage) {
          existingMessages.push(newMessage);
        } else {
          existingMessage.status = newMessage.status;
          existingMessage.value = newMessage.value;
          existingMessage.citations = newMessage.citations;
          existingMessage.references = newMessage.references;
          existingMessage.actions = newMessage.actions;
          existingMessage.appliedFilters = newMessage.appliedFilters;
        }
      });
      draft.messages.items[payload.researchId] = existingMessages.filter(
        (existingMessage) => existingMessage.status === "completed",
      );
      draft.messages.loading = false;
    });
  }

  async refreshFileReferences(payload: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.fileReferences.loading = true;
    });
    const { data } = await this.apiService.getReferences(payload);
    if (!data) throw new Error("No data");
    this.updateState((draft) => {
      if (!draft.fileReferences.items[payload.researchId]) {
        draft.fileReferences.items[payload.researchId] = [];
      }

      draft.fileReferences.items[payload.researchId] = data.references.filter(
        (reference, index, self) =>
          index ===
          self.findIndex(
            (r) =>
              r.title === reference.title &&
              r.sourceUrl === reference.sourceUrl,
          ),
      );
      draft.fileReferences.loading = false;
    });
  }

  async removeReferences(payload: {
    researchId: ResearchId;
    referenceIds: string[];
  }) {
    this.productEvents.dispatchEvent(new ProductEvent("reference_remove"));
    await this.apiService.removeReferences(payload);
    await Promise.all([
      this.refreshFileReferences({ researchId: payload.researchId }),
      this.refreshMessages({ researchId: payload.researchId }),
    ]);
  }

  async refreshFilters(payload: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.filters.loading = true;
    });
    const { data, success } = await this.apiService.getFilters({
      researchId: payload.researchId,
    });
    if (!success) {
      console.log(`Error fetching filters for research ${payload.researchId}`);
      return;
    }
    if (!data) throw new Error("No data");
    this.updateState((draft) => {
      draft.filters.items[payload.researchId] = data;
      draft.filters.loading = false;
    });
  }

  async refreshFilterOptions(payload: {
    researchId: ResearchId;
  }): Promise<void> {
    this.updateState((draft) => {
      draft.filterOptions.loading = true;
      delete draft.filterOptions.items;
    });
    const { data: dataOptions } = await this.apiService.getFilterOptions({
      researchId: payload.researchId,
    });
    if (!dataOptions) throw new Error("No data");
    this.updateState((draft) => {
      if (dataOptions.filterOptionsList) {
        delete draft.filterOptions.items;
        draft.filterOptions.list = dataOptions.filterOptionsList || [];
      } else {
        delete draft.filterOptions.list;
        draft.filterOptions.items = dataOptions.filterOptions;
      }
      draft.filterOptions.loading = false;
    });
  }
  async refreshKnowledgeSources() {
    this.updateState((draft) => {
      draft.knowledgeSources.loading = true;
    });
    const { data } = await this.apiService.getKnowledgeSources();
    this.updateState((draft) => {
      draft.knowledgeSources.loading = false;
      if (!data) return;
      draft.knowledgeSources.items = data.knowledgeSources;
    });
  }

  private pickResearchMessages(
    appState: AppState,
    payload: { researchId: ResearchId },
  ): Array<Message> {
    let messages = appState.messages.items[payload.researchId];
    if (!messages) messages = appState.messages.items[payload.researchId] = [];
    return messages;
  }

  async setFilters(param: { researchId: ResearchId; filters: FilterState }) {
    this.productEvents.dispatchEvent(new ProductEvent("filter_change"));
    this.updateState((draft) => {
      draft.filters.loading = true;
      draft.filters.items[param.researchId] = param.filters;
    });
    await this.apiService.setFilters(param);
    await this.refreshFilters({
      researchId: param.researchId,
    });
  }

  async regenerateSummary(param: { researchId: ResearchId }) {
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research) return;
      research.regenerating_summary = true;
      const messages = this.pickResearchMessages(draft, param);
      messages.push({
        id: "optimistic_summary_request",
        status: "optimistic",
        value: "Regenerate summary.",
        sender: "user",
        create_time: new Date().toISOString(),
      });
      messages.push({
        id: "pending_summary_response",
        status: "optimistic",
        sender: "agent",
        create_time: new Date().toISOString(),
      });
    });
    await this.apiService.regenerateSummary(param);
    await this.refreshMessages({ researchId: param.researchId });
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research) return;
      research.regenerating_summary = false;
    });
  }

  async deleteResearch(param: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.researchMap.loading = true;
      delete draft.researchMap.items[param.researchId];
    });
    await this.apiService.deleteResearch(param);
    await this.refreshResearches();
  }

  async renameResearch(param: { researchId: ResearchId; name: string }) {
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research) return;
      research.display_name = param.name;
      research.update_time = new Date().toISOString();
    });
    const result = await this.apiService.renameResearch({
      researchId: param.researchId,
      name: param.name,
    });
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research || !result.data) return;
      research.display_name = result.data?.research.display_name || "";
      research.update_time = result.data.research.update_time;
    });
  }

  async refreshTermsAndConditions(input?: {
    forceContentLoad?: boolean;
  }): Promise<void> {
    this.updateState((draft) => {
      draft.termsAndConditions.loading = true;
      draft.termsAndConditions.error = null;
    });
    try {
      // Get status first
      const statusResult = await this.apiService.getTermsAndConditionsStatus({
        appContext: getAppContext(
          this.enableClaMode,
          this.enableProductTestingBusinessDomain,
        ),
      });

      if (statusResult.success && statusResult.data) {
        const status = statusResult.data.status;
        this.updateState((draft) => {
          draft.termsAndConditions.status = status;
        });

        // If terms are pending, also fetch the content
        // If forceContentLoad is true, fetch the content regardless of status
        if (status === "pending_acceptance" || input?.forceContentLoad) {
          const contentResult =
            await this.apiService.getLatestTermsAndConditions({
              appContext: getAppContext(
                this.enableClaMode,
                this.enableProductTestingBusinessDomain,
              ),
            });
          if (contentResult.success && contentResult.data) {
            const content = contentResult.data.content;
            this.updateState((draft) => {
              draft.termsAndConditions.content = content;
            });
          }
        }
      } else {
        this.updateState((draft) => {
          draft.termsAndConditions.error =
            "Failed to load terms and conditions. Please try again.";
        });
      }
    } catch (error) {
      console.log("Error fetching terms and conditions", error);
      this.updateState((draft) => {
        draft.termsAndConditions.error =
          "Failed to load terms and conditions. Please try again.";
      });
    } finally {
      this.updateState((draft) => {
        draft.termsAndConditions.loading = false;
      });
    }
  }

  async acceptTermsAndConditions(): Promise<void> {
    this.updateState((draft) => {
      draft.termsAndConditions.accepting = true;
    });
    const failedToAccept = (error?: Error) => {
      this.updateState((draft) => {
        draft.termsAndConditions.status = "error";
        draft.termsAndConditions.error =
          "Failed to accept terms and conditions. Please try again later.";
        console.error("Failed to accept terms and conditions", error);
      });
    };
    try {
      const result = await this.apiService.acceptTermsAndConditions({
        appContext: getAppContext(
          this.enableClaMode,
          this.enableProductTestingBusinessDomain,
        ),
      });
      if (result.success) {
        this.updateState((draft) => {
          draft.termsAndConditions.status = "accepted";
        });
      } else {
        failedToAccept();
      }
    } catch (error) {
      failedToAccept(error as Error);
    } finally {
      this.updateState((draft) => {
        draft.termsAndConditions.accepting = false;
      });
    }
  }
}
